/**
 * Model manifest — the single source of truth for every GLB asset the
 * `ModelLibrary` can load.
 *
 * Each entry maps a stable, consumer-facing name to the file that ships under
 * `public/models/`, plus the provenance the asset-pipeline spec requires
 * (pack, source URL, license). Names are resolved through `MODEL_ALIASES`
 * first, so consumers can use either a canonical name or a short alias.
 *
 * This module is intentionally dependency-free (no `three` import) so the
 * headless check script can import it directly through Node's TypeScript type
 * stripping.
 */

/** Provenance + file info for one model. The record key is the model name. */
export interface ModelManifestEntry {
  /** Path relative to the models base URL (default `/models/`). */
  file: string;
  /** Asset pack the model came from (name + version). */
  pack: string;
  /** Upstream page/download the model was sourced from. */
  sourceUrl: string;
  /** License short name, e.g. `CC0`. */
  license: string;
  /**
   * Extra LOD level names, ordered near -> far. Level 0 is always this entry
   * itself, so `buildLod` constructs a level per name in `[self, ...lod]`.
   */
  lod?: string[];
  /** True when the model has skinned meshes (cloned via `SkeletonUtils`). */
  skinned?: boolean;
}

/**
 * Short aliases for the adopted packs. `resolveModelName` maps an alias to its
 * canonical manifest name so consumers can request a friendly key.
 */
export const MODEL_ALIASES: Readonly<Record<string, string>> = {
  rock: 'island-rock',
  hull: 'hull-piece',
  'pirate-crew': 'crew',
  rowboat: 'boat-row-large',

  // --- Hero Ship & Poly Haven realistic assets ----------------------------
  'hero-hull': 'ghost_pirate_ship',
  'ghost-ship': 'ghost_pirate_ship',
  'real-ship': 'dutch_ship_medium',
  'hero-cannon': 'cannon_01',
  'real-cannon': 'cannon_01',
  'deck-barrel': 'wooden_barrels_01',
  'real-barrel': 'wooden_barrels_01',
  'deck-crate': 'wooden_crate_02',
  'real-crate': 'wooden_crate_02',
  'island-cliff': 'coastal_cliff_01',
  'real-cliff': 'coastal_cliff_01',
  'island-boulder': 'boulder_01',
  'real-rock': 'boulder_01',
  'dead-tree': 'dead_tree_trunk_02',
  'real-tree': 'dead_tree_trunk_02',
};

/**
 * The canonical model table. Keys are the names consumers pass to
 * `ModelLibrary.load` / `create` / `instance`. All referenced files ship under
 * `public/models/` (see `public/models/LICENSES.md` for download sources).
 */
export const MODEL_MANIFEST: Readonly<Record<string, ModelManifestEntry>> = {
  // --- Kenney Pirate Kit 2.1 (CC0) --------------------------------------
  crate: {
    file: 'kenney-pirate/crate.glb',
    pack: 'Kenney Pirate Kit 2.1',
    sourceUrl: 'https://kenney.nl/assets/pirate-kit',
    license: 'CC0',
  },
  barrel: {
    file: 'kenney-pirate/barrel.glb',
    pack: 'Kenney Pirate Kit 2.1',
    sourceUrl: 'https://kenney.nl/assets/pirate-kit',
    license: 'CC0',
  },
  cannon: {
    file: 'kenney-pirate/cannon.glb',
    pack: 'Kenney Pirate Kit 2.1',
    sourceUrl: 'https://kenney.nl/assets/pirate-kit',
    license: 'CC0',
  },
  'cannon-ball': {
    file: 'kenney-pirate/cannon-ball.glb',
    pack: 'Kenney Pirate Kit 2.1',
    sourceUrl: 'https://kenney.nl/assets/pirate-kit',
    license: 'CC0',
  },
  chest: {
    file: 'kenney-pirate/chest.glb',
    pack: 'Kenney Pirate Kit 2.1',
    sourceUrl: 'https://kenney.nl/assets/pirate-kit',
    license: 'CC0',
  },
  'island-rock': {
    file: 'kenney-pirate/island-rock.glb',
    pack: 'Kenney Pirate Kit 2.1',
    sourceUrl: 'https://kenney.nl/assets/pirate-kit',
    license: 'CC0',
  },
  'island-rock-b': {
    file: 'kenney-pirate/island-rock-b.glb',
    pack: 'Kenney Pirate Kit 2.1',
    sourceUrl: 'https://kenney.nl/assets/pirate-kit',
    license: 'CC0',
  },
  'island-rock-c': {
    file: 'kenney-pirate/island-rock-c.glb',
    pack: 'Kenney Pirate Kit 2.1',
    sourceUrl: 'https://kenney.nl/assets/pirate-kit',
    license: 'CC0',
  },
  palm: {
    file: 'kenney-pirate/palm.glb',
    pack: 'Kenney Pirate Kit 2.1',
    sourceUrl: 'https://kenney.nl/assets/pirate-kit',
    license: 'CC0',
  },
  'hull-piece': {
    file: 'kenney-pirate/hull-piece.glb',
    pack: 'Kenney Pirate Kit 2.1',
    sourceUrl: 'https://kenney.nl/assets/pirate-kit',
    license: 'CC0',
  },

  // --- Kenney Watercraft Kit 2.1 (CC0) -----------------------------------
  'boat-row-large': {
    file: 'kenney-watercraft/boat-row-large.glb',
    pack: 'Kenney Watercraft Kit 2.1',
    sourceUrl: 'https://kenney.nl/assets/watercraft-kit',
    license: 'CC0',
  },
  'boat-row-small': {
    file: 'kenney-watercraft/boat-row-small.glb',
    pack: 'Kenney Watercraft Kit 2.1',
    sourceUrl: 'https://kenney.nl/assets/watercraft-kit',
    license: 'CC0',
  },
  buoy: {
    file: 'kenney-watercraft/buoy.glb',
    pack: 'Kenney Watercraft Kit 2.1',
    sourceUrl: 'https://kenney.nl/assets/watercraft-kit',
    license: 'CC0',
  },

  // --- Quaternius Pirate Kit (CC0) ----------------------------------------
  // The Quaternius "Pirate Captain" character, re-hosted on Poly Pizza with a
  // direct GLB download (the upstream Quaternius site hosts the pack behind a
  // Google Drive folder). Skinned + animated: idle/walk/run/punch/sword/duck…
  crew: {
    file: 'quaternius-pirate/crew.glb',
    pack: 'Quaternius Pirate Kit — Pirate Captain',
    sourceUrl: 'https://poly.pizza/m/sN18LyyHAU',
    license: 'CC0',
    skinned: true,
  },

  // --- Poly Haven realistic assets (CC0, generated LOD chains) -------------
  // Downloaded + optimised by `scripts/optimize-models.mjs`: weld -> simplify
  // per ratio -> WebP albedo -> meshopt(medium). Level 0 is the entry itself;
  // `lod` lists the additional levels (near -> far).
  'dutch_ship_medium': {
    file: 'polyhaven/dutch_ship_medium.lod0.glb',
    pack: 'Poly Haven — Dutch Ship Medium',
    sourceUrl: 'https://polyhaven.com/a/dutch_ship_medium',
    license: 'CC0',
    lod: ['dutch_ship_medium-lod1', 'dutch_ship_medium-lod2'],
    skinned: false,
  },
  'dutch_ship_medium-lod1': {
    file: 'polyhaven/dutch_ship_medium.lod1.glb',
    pack: 'Poly Haven — Dutch Ship Medium',
    sourceUrl: 'https://polyhaven.com/a/dutch_ship_medium',
    license: 'CC0',
    skinned: false,
  },
  'dutch_ship_medium-lod2': {
    file: 'polyhaven/dutch_ship_medium.lod2.glb',
    pack: 'Poly Haven — Dutch Ship Medium',
    sourceUrl: 'https://polyhaven.com/a/dutch_ship_medium',
    license: 'CC0',
    skinned: false,
  },
  'cannon_01': {
    file: 'polyhaven/cannon_01.lod0.glb',
    pack: 'Poly Haven — Cannon 01',
    sourceUrl: 'https://polyhaven.com/a/cannon_01',
    license: 'CC0',
    skinned: false,
  },
  'wooden_barrels_01': {
    file: 'polyhaven/wooden_barrels_01.lod0.glb',
    pack: 'Poly Haven — Wooden Barrels 01',
    sourceUrl: 'https://polyhaven.com/a/wooden_barrels_01',
    license: 'CC0',
    lod: ['wooden_barrels_01-lod1'],
    skinned: false,
  },
  'wooden_barrels_01-lod1': {
    file: 'polyhaven/wooden_barrels_01.lod1.glb',
    pack: 'Poly Haven — Wooden Barrels 01',
    sourceUrl: 'https://polyhaven.com/a/wooden_barrels_01',
    license: 'CC0',
    skinned: false,
  },
  'wooden_crate_02': {
    file: 'polyhaven/wooden_crate_02.lod0.glb',
    pack: 'Poly Haven — Wooden Crate 02',
    sourceUrl: 'https://polyhaven.com/a/wooden_crate_02',
    license: 'CC0',
    lod: ['wooden_crate_02-lod1'],
    skinned: false,
  },
  'wooden_crate_02-lod1': {
    file: 'polyhaven/wooden_crate_02.lod1.glb',
    pack: 'Poly Haven — Wooden Crate 02',
    sourceUrl: 'https://polyhaven.com/a/wooden_crate_02',
    license: 'CC0',
    skinned: false,
  },
  'boulder_01': {
    file: 'polyhaven/boulder_01.lod0.glb',
    pack: 'Poly Haven — Boulder 01',
    sourceUrl: 'https://polyhaven.com/a/boulder_01',
    license: 'CC0',
    skinned: false,
  },
  'coastal_cliff_01': {
    file: 'polyhaven/coastal_cliff_01.lod0.glb',
    pack: 'Poly Haven — Coastal Cliff 01',
    sourceUrl: 'https://polyhaven.com/a/coastal_cliff_01',
    license: 'CC0',
    lod: ['coastal_cliff_01-lod1'],
    skinned: false,
  },
  'coastal_cliff_01-lod1': {
    file: 'polyhaven/coastal_cliff_01.lod1.glb',
    pack: 'Poly Haven — Coastal Cliff 01',
    sourceUrl: 'https://polyhaven.com/a/coastal_cliff_01',
    license: 'CC0',
    skinned: false,
  },
  'dead_tree_trunk_02': {
    file: 'polyhaven/dead_tree_trunk_02.lod0.glb',
    pack: 'Poly Haven — Dead Tree Trunk 02',
    sourceUrl: 'https://polyhaven.com/a/dead_tree_trunk_02',
    license: 'CC0',
    lod: ['dead_tree_trunk_02-lod1'],
    skinned: false,
  },
  'dead_tree_trunk_02-lod1': {
    file: 'polyhaven/dead_tree_trunk_02.lod1.glb',
    pack: 'Poly Haven — Dead Tree Trunk 02',
    sourceUrl: 'https://polyhaven.com/a/dead_tree_trunk_02',
    license: 'CC0',
    skinned: false,
  },
  'ghost_pirate_ship': {
    file: 'ghost-ship/ghost_pirate_ship.glb',
    pack: 'Meshy AI — Ghost Pirate Ship',
    sourceUrl: 'local:Models/Barco',
    license: 'Custom / Game Asset',
    skinned: false,
  },
};

/**
 * Resolve a consumer-supplied name (canonical name or alias) to the canonical
 * manifest key, or `null` when the name is unknown. This is the "missing model
 * -> null, never throw" contract at the manifest layer.
 */
export function resolveModelName(name: string): string | null {
  if (Object.hasOwn(MODEL_MANIFEST, name)) return name;
  if (Object.hasOwn(MODEL_ALIASES, name)) return MODEL_ALIASES[name];
  return null;
}

/** Return the manifest entry for a canonical name or alias, or `null`. */
export function getModelEntry(name: string): ModelManifestEntry | null {
  const key = resolveModelName(name);
  return key ? (MODEL_MANIFEST[key] ?? null) : null;
}

/** All canonical model names, in insertion order. */
export function getModelNames(): string[] {
  return Object.keys(MODEL_MANIFEST);
}
